// Formatting utilities

import { formatDistanceToNow, format, parseISO, differenceInHours, differenceInMinutes, differenceInDays } from 'date-fns'

export function formatProbability(prob: number | null | undefined): string {
  if (prob === null || prob === undefined) return '—'
  return `${(prob * 100).toFixed(1)}%`
}

export function formatEdge(edge: number | null | undefined): string {
  if (edge === null || edge === undefined) return '—'
  const sign = edge > 0 ? '+' : ''
  return `${sign}${(edge * 100).toFixed(1)}%`
}

export function formatEdgeBps(edge: number | null | undefined): string {
  if (edge === null || edge === undefined) return '—'
  const bps = Math.round(edge * 10000)
  const sign = bps > 0 ? '+' : ''
  return `${sign}${bps}bps`
}

export function formatPnL(pnl: number | null | undefined): string {
  if (pnl === null || pnl === undefined) return '—'
  const sign = pnl > 0 ? '+' : ''
  return `${sign}$${pnl.toFixed(2)}`
}

export function formatPnLPercent(pnl: number | null | undefined, basis: number | null | undefined): string {
  if (pnl === null || pnl === undefined || basis === null || basis === undefined || basis === 0) return '—'
  const pct = (pnl / basis) * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

export function formatTrend(current: number | null | undefined, previous: number | null | undefined): { text: string; direction: 'up' | 'down' | 'flat' } {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return { text: '—', direction: 'flat' }
  }
  const change = current - previous
  const pctChange = previous !== 0 ? (change / Math.abs(previous)) * 100 : 0

  if (Math.abs(pctChange) < 1) {
    return { text: '→ flat', direction: 'flat' }
  }

  const sign = change > 0 ? '+' : ''
  return {
    text: `${change > 0 ? '↑' : '↓'} ${sign}${pctChange.toFixed(0)}%`,
    direction: change > 0 ? 'up' : 'down'
  }
}

export function formatQuantity(qty: number | null | undefined): string {
  if (qty === null || qty === undefined) return '—'
  return qty.toFixed(2)
}

export function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    const date = parseISO(dateStr)
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return '—'
  }
}

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    const date = parseISO(dateStr)
    return format(date, 'MMM d, yyyy HH:mm')
  } catch {
    return '—'
  }
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  try {
    const date = parseISO(dateStr)
    return format(date, 'MMM d, yyyy')
  } catch {
    return '—'
  }
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60000)
  const secs = Math.round((ms % 60000) / 1000)
  return `${mins}m ${secs}s`
}

export function getEdgeClass(edge: number | null | undefined): string {
  if (edge === null || edge === undefined) return 'edge-neutral'
  if (Math.abs(edge) < 0.01) return 'edge-neutral'
  return edge > 0 ? 'edge-positive' : 'edge-negative'
}

export function getActionBadgeClass(action: string): string {
  switch (action) {
    case 'hold':
      return 'badge-muted'
    case 'add':
      return 'badge-green'
    case 'reduce':
      return 'badge-amber'
    case 'exit':
      return 'badge-red'
    default:
      return 'badge-muted'
  }
}

export function getUrgencyBadgeClass(urgency: string): string {
  switch (urgency) {
    case 'routine':
      return 'badge-muted'
    case 'soon':
      return 'badge-amber'
    case 'immediate':
      return 'badge-red'
    default:
      return 'badge-muted'
  }
}

export function getThesisStatusBadgeClass(status: string): string {
  switch (status) {
    case 'intact':
      return 'badge-green'
    case 'strengthened':
      return 'badge-green'
    case 'weakening':
      return 'badge-amber'
    case 'broken':
      return 'badge-red'
    default:
      return 'badge-muted'
  }
}

export function getAgentIcon(agentName: string): string {
  switch (agentName) {
    case 'planner':
      return '📋'
    case 'news':
      return '📰'
    case 'quant':
      return '📊'
    case 'skeptic':
      return '🔍'
    case 'synthesis':
      return '🧬'
    case 'validator':
      return '✓'
    default:
      return '⚙️'
  }
}

export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 3) + '...'
}

// Time countdown formatting with urgency levels
export interface TimeCountdown {
  text: string
  urgency: 'critical' | 'warning' | 'normal' | 'relaxed'
  hoursRemaining: number | null
}

export function formatTimeCountdown(dateStr: string | null | undefined): TimeCountdown {
  if (!dateStr) return { text: '—', urgency: 'normal', hoursRemaining: null }

  try {
    const date = parseISO(dateStr)
    const now = new Date()

    if (date < now) {
      return { text: 'Closed', urgency: 'normal', hoursRemaining: 0 }
    }

    const hoursRemaining = differenceInHours(date, now)
    const minutesRemaining = differenceInMinutes(date, now)
    const daysRemaining = differenceInDays(date, now)

    let text: string
    let urgency: 'critical' | 'warning' | 'normal' | 'relaxed'

    if (minutesRemaining < 60) {
      text = `${minutesRemaining}m`
      urgency = 'critical'
    } else if (hoursRemaining < 6) {
      text = `${hoursRemaining}h ${minutesRemaining % 60}m`
      urgency = 'critical'
    } else if (hoursRemaining < 24) {
      text = `${hoursRemaining}h`
      urgency = 'warning'
    } else if (daysRemaining < 7) {
      text = `${daysRemaining}d ${hoursRemaining % 24}h`
      urgency = 'normal'
    } else {
      text = `${daysRemaining}d`
      urgency = 'relaxed'
    }

    return { text, urgency, hoursRemaining }
  } catch {
    return { text: '—', urgency: 'normal', hoursRemaining: null }
  }
}

export function getTimeCountdownClass(urgency: TimeCountdown['urgency']): string {
  switch (urgency) {
    case 'critical':
      return 'text-signal-red animate-pulse'
    case 'warning':
      return 'text-signal-amber'
    case 'normal':
      return 'text-star-muted'
    case 'relaxed':
      return 'text-star-dim'
  }
}

// Spread formatting
export function formatSpread(spreadBps: number | null | undefined): string {
  if (spreadBps === null || spreadBps === undefined) return '—'
  return `${(spreadBps / 100).toFixed(1)}%`
}

export function getSpreadClass(spreadBps: number | null | undefined): string {
  if (spreadBps === null || spreadBps === undefined) return 'text-star-muted'
  if (spreadBps < 100) return 'text-signal-green'
  if (spreadBps < 300) return 'text-star-muted'
  return 'text-signal-amber'
}

// Opportunity score calculation
export function calculateOpportunityScore(
  edge: number | null | undefined,
  liquidity: number | null | undefined,
  confidence: number | null | undefined,
  spreadBps: number | null | undefined
): number | null {
  if (edge === null || edge === undefined) return null

  const absEdge = Math.abs(edge)
  const edgeScore = Math.min(absEdge * 500, 40) // Max 40 points from edge

  const liqScore = liquidity
    ? Math.min(Math.log10(liquidity + 1) * 5, 25) // Max 25 points from liquidity
    : 10

  const confScore = confidence
    ? confidence * 25 // Max 25 points from confidence
    : 12.5

  const spreadPenalty = spreadBps
    ? Math.max(0, 10 - spreadBps / 50) // Subtract up to 10 points for high spread
    : 5

  return Math.round(Math.min(100, edgeScore + liqScore + confScore + spreadPenalty))
}

export function getOpportunityScoreClass(score: number | null): string {
  if (score === null) return 'text-star-muted'
  if (score >= 70) return 'text-signal-green'
  if (score >= 50) return 'text-signal-blue'
  if (score >= 30) return 'text-star-muted'
  return 'text-star-dim'
}

// Brier score helpers
export function getBrierClass(brier: number | null | undefined): string {
  if (brier === null || brier === undefined) return 'text-star-muted'
  if (brier < 0.1) return 'text-signal-green'
  if (brier < 0.15) return 'text-signal-blue'
  if (brier < 0.25) return 'text-signal-amber'
  return 'text-signal-red'
}

export function getBrierLabel(brier: number | null | undefined): string {
  if (brier === null || brier === undefined) return 'No data'
  if (brier < 0.1) return 'Excellent'
  if (brier < 0.15) return 'Good'
  if (brier < 0.25) return 'Fair'
  return 'Poor'
}

// Probability zone helpers for calibration training
export type ProbabilityZone = 'mid' | 'moderate_low' | 'moderate_high' | 'tail_low' | 'tail_high' | 'extreme_low' | 'extreme_high'

export type TierMode = 'trade' | 'small_only' | 'record_only'

export interface ZoneInfo {
  zone: ProbabilityZone
  mode: TierMode
  label: string
  emoji: string
  description: string
  minEdgeBps: number
  maxSizeFactor: number
}

export function getProbabilityZone(prob: number | null | undefined): ZoneInfo | null {
  if (prob === null || prob === undefined) return null

  // Zone definitions matching backend
  if (prob >= 0.35 && prob < 0.65) {
    return {
      zone: 'mid',
      mode: 'trade',
      label: 'Mid (35-65%)',
      emoji: '🎯',
      description: 'Optimal zone for calibration training. Low variance, fast feedback.',
      minEdgeBps: 250,
      maxSizeFactor: 1.0,
    }
  }
  if (prob >= 0.20 && prob < 0.35) {
    return {
      zone: 'moderate_low',
      mode: 'trade',
      label: 'Mod Low (20-35%)',
      emoji: '📉',
      description: 'Moderate probability events. Higher edge required.',
      minEdgeBps: 500,
      maxSizeFactor: 0.5,
    }
  }
  if (prob >= 0.65 && prob < 0.80) {
    return {
      zone: 'moderate_high',
      mode: 'trade',
      label: 'Mod High (65-80%)',
      emoji: '📈',
      description: 'Moderate probability events. Higher edge required.',
      minEdgeBps: 500,
      maxSizeFactor: 0.5,
    }
  }
  if (prob >= 0.10 && prob < 0.20) {
    return {
      zone: 'tail_low',
      mode: 'small_only',
      label: 'Tail Low (10-20%)',
      emoji: '⚠️',
      description: 'High variance zone. Small positions only.',
      minEdgeBps: 800,
      maxSizeFactor: 0.25,
    }
  }
  if (prob >= 0.80 && prob < 0.90) {
    return {
      zone: 'tail_high',
      mode: 'small_only',
      label: 'Tail High (80-90%)',
      emoji: '⚠️',
      description: 'High variance zone. Small positions only.',
      minEdgeBps: 800,
      maxSizeFactor: 0.25,
    }
  }
  if (prob < 0.10) {
    return {
      zone: 'extreme_low',
      mode: 'record_only',
      label: 'Extreme Low (<10%)',
      emoji: '🔴',
      description: 'Recording for calibration only. Not trading due to extreme variance.',
      minEdgeBps: 1500,
      maxSizeFactor: 0.1,
    }
  }
  // prob >= 0.90
  return {
    zone: 'extreme_high',
    mode: 'record_only',
    label: 'Extreme High (>90%)',
    emoji: '🔴',
    description: 'Recording for calibration only. Not trading due to extreme variance.',
    minEdgeBps: 1500,
    maxSizeFactor: 0.1,
  }
}

export function getZoneClass(zone: ProbabilityZone | null | undefined): string {
  if (!zone) return 'text-star-muted'
  switch (zone) {
    case 'mid':
      return 'text-signal-green'
    case 'moderate_low':
    case 'moderate_high':
      return 'text-signal-blue'
    case 'tail_low':
    case 'tail_high':
      return 'text-signal-amber'
    case 'extreme_low':
    case 'extreme_high':
      return 'text-signal-red'
    default:
      return 'text-star-muted'
  }
}

export function getZoneBadgeClass(mode: TierMode): string {
  switch (mode) {
    case 'trade':
      return 'bg-signal-green/20 text-signal-green border-signal-green/30'
    case 'small_only':
      return 'bg-signal-amber/20 text-signal-amber border-signal-amber/30'
    case 'record_only':
      return 'bg-signal-red/20 text-signal-red border-signal-red/30'
  }
}

export function getModeLabel(mode: TierMode): string {
  switch (mode) {
    case 'trade':
      return 'Trade'
    case 'small_only':
      return 'Small Only'
    case 'record_only':
      return 'Record Only'
  }
}

// Staleness helpers for positions without recent reviews
export type StalenessLevel = 'fresh' | 'stale' | 'critical'

export interface StalenessInfo {
  level: StalenessLevel
  label: string
  hoursAgo: number
}

export function getPositionStaleness(lastReviewAt: string | null | undefined): StalenessInfo | null {
  if (!lastReviewAt) {
    return {
      level: 'critical',
      label: 'No review',
      hoursAgo: Infinity,
    }
  }

  try {
    const reviewDate = parseISO(lastReviewAt)
    const now = new Date()
    const hoursAgo = differenceInHours(now, reviewDate)

    if (hoursAgo < 24) {
      return {
        level: 'fresh',
        label: 'Recent',
        hoursAgo,
      }
    } else if (hoursAgo < 72) {
      return {
        level: 'stale',
        label: `${Math.floor(hoursAgo / 24)}d ago`,
        hoursAgo,
      }
    } else {
      return {
        level: 'critical',
        label: `${Math.floor(hoursAgo / 24)}d ago`,
        hoursAgo,
      }
    }
  } catch {
    return null
  }
}

export function getStalenessClass(level: StalenessLevel): string {
  switch (level) {
    case 'fresh':
      return 'text-signal-green'
    case 'stale':
      return 'text-signal-amber'
    case 'critical':
      return 'text-signal-red'
  }
}

export function getStalenessBadgeClass(level: StalenessLevel): string {
  switch (level) {
    case 'fresh':
      return 'bg-signal-green/10 text-signal-green border-signal-green/30'
    case 'stale':
      return 'bg-signal-amber/10 text-signal-amber border-signal-amber/30'
    case 'critical':
      return 'bg-signal-red/10 text-signal-red border-signal-red/30'
  }
}
